/**
 * POM-6536 — what sharing a lot across the batch does to each classified row.
 *
 * A pass over classifyRows' output rather than a gate inside it: the classifier judges one row
 * against its own lot, and is at the line cap. The judgement here needs the whole batch, which
 * weighBatchLots supplies.
 */

import SYSTEM_DEFICIT from 'common/constants/inventoryLabels';
import { ROW_STATUS, UNUSABLE_REASON, formatQty, statusSortRank } from './bulkLotNdcStatus';

export const BATCH_ROW_STATUS = {
  /** The row's lot covers it alone, but the rows ahead of it in this batch leave too little. */
  SHORT_IN_BATCH: 'SHORT_IN_BATCH',
  /** A claim on the row's own lot is staged: no lot change, so no label rebuild. */
  CLAIM_PENDING: 'CLAIM_PENDING',
};

// Rows the batch can push past their lot's stock. Problem rows already need a decision of their own.
const FLAGGED_WHEN_SHORT = new Set([ROW_STATUS.AS_ALLOCATED, ROW_STATUS.NO_ALTERNATIVE]);

const deficitWord = SYSTEM_DEFICIT.toLowerCase();

const isPartialRow = (row) =>
  row.status === ROW_STATUS.UNUSABLE_LOT && row.unusableReason === UNUSABLE_REASON.SHORT_FOR_FILL;

export const batchStatusSortRank = (status) => {
  if (status === BATCH_ROW_STATUS.SHORT_IN_BATCH)
    return statusSortRank(ROW_STATUS.NO_USABLE_LOT) + 0.5;
  if (status === BATCH_ROW_STATUS.CLAIM_PENDING) return statusSortRank(ROW_STATUS.CHANGED) + 0.5;

  return statusSortRank(status);
};

const claimPendingRow = ({ row, reason, shortQty, isOwnClaim }) => ({
  ...row,
  status: BATCH_ROW_STATUS.CLAIM_PENDING,
  canEdit: true,
  blocksBatch: false,
  printsWithoutLot: false,
  isClaimPending: isOwnClaim,
  claimsShortfall: true,
  deficitQty: shortQty,
  batchLotDetail: 'Pending commit · label unchanged',
  reason,
});

const shortInBatchReason = ({ lot, shortQty, canClaim, canEdit, claimsEnabled }) => {
  const lead = `${formatQty(shortQty)} short on lot ${lot.lotNumber} in this batch`;

  if (canClaim)
    return `${lead} — open it to claim ${formatQty(shortQty)} as ${deficitWord}, or pick another lot`;
  // Core refuses the whole lot's claim when one row on it cannot carry the permission.
  if (claimsEnabled && !lot.canClaim && canEdit) {
    return `${lead} — another row on it holds two lots or a hand-recorded entry, so it cannot be claimed. Pick another lot`;
  }

  return canEdit ? `${lead} — pick another lot` : `${lead} — no other lot here covers it`;
};

/**
 * A committed claim belongs to the lot: once every row on it carries the permission, any of them
 * may be the one that fills last. A row on it without the permission is flagged Short in batch.
 */
const restateCommittedClaim = ({ row, usage }) => {
  const lot = usage.lots.get(row.item.recordedLots?.[0]?.inventoryLotId);
  if (!lot || lot.shortQty <= 0 || lot.rowCount < 2 || !lot.isEveryRowPermitted) return row;

  return {
    ...row,
    reason: `Lot ${lot.lotNumber} is ${formatQty(lot.shortQty)} short across ${lot.rowCount} rows in this batch — claimed as ${deficitWord} on whichever row fills last`,
  };
};

const restateChangedRow = ({ row, lot, shortQty, quantityOnLot }) => {
  if (row.recordsUnverifiedLot) return row;

  if (lot.isClaimed) {
    return {
      ...row,
      claimsShortfall: true,
      deficitQty: shortQty,
      reason: `${formatQty(quantityOnLot - shortQty)} from lot ${lot.lotNumber} + ${formatQty(shortQty)} as ${deficitWord} — commit to regenerate the label`,
    };
  }

  return {
    ...row,
    reason: `Lot ${lot.lotNumber} is ${formatQty(shortQty)} short in this batch — reopen it to claim the rest, or pick another lot`,
  };
};

// Worded on the row's own quantity on the lot, not its fill: a two-lot row asks this lot for part.
const restateKeptRow = ({ row, lot, shortQty, quantityOnLot, isManualLotEnabled }) => {
  const freeQty = quantityOnLot - shortQty;

  if (lot.isClaimed) {
    return claimPendingRow({
      row,
      shortQty,
      isOwnClaim: false,
      reason: `Covered by the claim staged on lot ${lot.lotNumber} — ${formatQty(shortQty)} will be claimed as ${deficitWord} when it fills`,
    });
  }

  // Today's single-row partial, worded with what the batch leaves rather than the whole shelf.
  if (isPartialRow(row)) {
    if (lot.rowCount < 2) return row;

    return {
      ...row,
      reason: `Lot ${lot.lotNumber} has ${formatQty(freeQty)} of ${formatQty(quantityOnLot)} free in this batch — pick it to dispense ${formatQty(freeQty)} and claim ${formatQty(shortQty)} as ${deficitWord}`,
    };
  }

  if (!FLAGGED_WHEN_SHORT.has(row.status)) return row;

  const claimsEnabled = isManualLotEnabled === true && row.item.canRecordUnverifiedLot === true;
  const canClaim = claimsEnabled && lot.canClaim;
  const canEdit = row.canEdit || canClaim;

  return {
    ...row,
    status: BATCH_ROW_STATUS.SHORT_IN_BATCH,
    canEdit,
    blocksBatch: true,
    printsWithoutLot: false,
    batchLotDetail: `${formatQty(freeQty)} of ${formatQty(quantityOnLot)} free in this batch`,
    reason: shortInBatchReason({ lot, shortQty, canClaim, canEdit, claimsEnabled }),
  };
};

const restateRow = ({ row, usage, claims, isManualLotEnabled }) => {
  const id = row.item.requestLineItemId;
  const short = usage.shortByLineItem.get(id);
  const shortQty = short?.shortQty ?? 0;

  if (claims?.[id]) {
    return claimPendingRow({
      row,
      shortQty,
      isOwnClaim: true,
      reason: `${formatQty(shortQty)} will be claimed as ${deficitWord} on lot ${
        usage.lots.get(claims[id])?.lotNumber ?? ''
      } when it fills. The label does not change.`,
    });
  }

  if (row.status === ROW_STATUS.SHORTFALL_COMMITTED) return restateCommittedClaim({ row, usage });
  if (!short) return row;

  const lot = usage.lots.get(short.inventoryLotId);
  const { quantityOnLot } = short;
  if (row.status === ROW_STATUS.CHANGED) {
    return restateChangedRow({ row, lot, shortQty, quantityOnLot });
  }

  return restateKeptRow({ row, lot, shortQty, quantityOnLot, isManualLotEnabled });
};

/**
 * classifyRows' result with the batch applied: the same `rows` / `unlistedRows` / `counts` shape,
 * plus `pendingDeficitQty`, the exact quantity the staged changes and claims will claim.
 *
 * `counts.changed` includes the staged claims: a claim holds the print bar and asks to be
 * committed like any edit. `counts.claimed` says how many of them are claims.
 */
export const applyBatchLotUsage = ({ classified, usage, claims, isManualLotEnabled }) => {
  const all = [...classified.rows, ...classified.unlistedRows].map((row) =>
    restateRow({ row, usage, claims, isManualLotEnabled })
  );
  const isUnlisted = (row) => row.status === ROW_STATUS.NO_ALTERNATIVE;
  const rows = all
    .filter((row) => !isUnlisted(row))
    .sort((left, right) => batchStatusSortRank(left.status) - batchStatusSortRank(right.status));
  const claimCount = Object.keys(claims ?? {}).length;
  const blocked = all.filter((row) => row.blocksBatch).length;

  return {
    rows,
    unlistedRows: all.filter(isUnlisted),
    counts: {
      ...classified.counts,
      listed: rows.length,
      autoIncluded: classified.counts.reviewed - rows.length,
      blocked,
      printable: classified.counts.total - blocked,
      withoutLot: rows.filter((row) => row.printsWithoutLot && !row.blocksBatch).length,
      changed: classified.counts.changed + claimCount,
      claimed: claimCount,
    },
    pendingDeficitQty: all.reduce((total, row) => {
      if (row.status === ROW_STATUS.CHANGED && row.recordsUnverifiedLot) {
        return total + Number(row.item.fillQuantity || 0);
      }

      return total + (row.deficitQty ?? 0);
    }, 0),
  };
};
